// src/components/HourPicker.js

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const LeftArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10.293 4.293a1 1 0 011.414 1.414L8.414 8H15a1 1 0 110 2H8.414l3.293 3.293a1 1 0 01-1.414 1.414l-5-5a1 1 0 010-1.414l5-5z" clipRule="evenodd" />
  </svg>
);

export default function HourPicker({ availabilitySlots, selectedDate, onSelect, onBack }) {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAvailableHours = async () => {
      try {
        setLoading(true);
        
        if (!selectedDate?.date || !availabilitySlots) {
          console.error('Missing required props:', { selectedDate, availabilitySlots });
          setHours([]);
          return;
        }

        // Get the day of the week (0-6, where 0 is Sunday)
        const dayOfWeek = selectedDate.date.getDay();
        
        // Filter available slots for the selected day
        const availableSlots = availabilitySlots.filter(slot => {
          const isAvailable = slot.dayOfWeek === dayOfWeek && slot.isAvailable;
          return isAvailable;
        });

        // Get all hours from available slots and sort them
        const availableHours = availableSlots
          .map(slot => slot.hour)
          .sort((a, b) => a - b);
        
        setHours(availableHours);
      } catch (error) {
        console.error('Error fetching available hours:', error);
        setHours([]);
      } finally {
        setLoading(false);
      }
    };

    if (selectedDate?.date) {
      fetchAvailableHours();
    }
  }, [selectedDate, availabilitySlots]);

  const handleHourSelect = (hour) => {
    onSelect({ hour });
  };

  const handleBack = () => {
    console.log('HourPicker: back button clicked');
    console.log('HourPicker: onBack prop type:', typeof onBack);
    if (typeof onBack === 'function') {
      console.log('HourPicker: calling onBack function');
      onBack();
    } else {
      console.log('HourPicker: onBack is not a function');
    }
  };

  if (!selectedDate?.date) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 text-lg">Selecione uma data primeiro.</p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleBack}
          className="mt-4 text-[#e91e63] hover:text-[#d81557] flex items-center justify-center mx-auto"
        >
          <LeftArrow /> Voltar
        </motion.button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e91e63] mx-auto"></div>
        <p className="mt-4 text-gray-600 text-lg">Carregando horários disponíveis...</p>
      </div>
    );
  }

  if (hours.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 text-lg">Nenhum horário disponível para esta data.</p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleBack}
          className="mt-4 text-[#e91e63] hover:text-[#d81557] flex items-center justify-center mx-auto"
        >
          <LeftArrow /> Voltar
        </motion.button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Escolha o horário</h2>
        <p className="text-gray-600">
          Selecione um horário disponível para {selectedDate.date.toLocaleDateString('pt-BR')}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {hours.map(hour => (
          <motion.button
            key={hour}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleHourSelect(hour)}
            className="p-4 rounded-xl border border-gray-200 hover:border-[#e91e63] hover:bg-[#e91e63]/5 transition-colors text-lg font-medium"
          >
            {hour.toString().padStart(2, '0')}:00
          </motion.button>
        ))}
      </div>

      <div className="flex justify-between pt-6">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleBack}
          className="rounded-xl py-3 px-6 flex items-center border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          <LeftArrow /> Voltar
        </motion.button>
      </div>
    </div>
  );
}
